import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  workflowDefinitionsApi, workflowInstancesApi, workflowTasksApi,
  type CreateWorkflowDefinitionRequest, type WorkflowDefinitionQuery,
  type WorkflowDocumentType, type WorkflowStepInput, type WorkflowTaskQuery,
} from '@/api/workflows.api';

export type WorkflowTaskDecision = 'check' | 'approve' | 'reject' | 'return';

export const workflowKeys = {
  all: ['workflows'] as const,
  definitions: (query: WorkflowDefinitionQuery) => [...workflowKeys.all, 'definitions', query] as const,
  definition: (id: string) => [...workflowKeys.all, 'definition', id] as const,
  tasks: (query: WorkflowTaskQuery) => [...workflowKeys.all, 'tasks', query] as const,
  task: (id: string) => [...workflowKeys.all, 'task', id] as const,
  instance: (id: string) => [...workflowKeys.all, 'instance', id] as const,
  document: (type: WorkflowDocumentType, id: string) => [...workflowKeys.all, 'document', type, id] as const,
};

export const useWorkflowDefinitions = (query: WorkflowDefinitionQuery) => useQuery({ queryKey: workflowKeys.definitions(query), queryFn: () => workflowDefinitionsApi.list(query) });
export const useWorkflowDefinition = (id?: string) => useQuery({ queryKey: workflowKeys.definition(id ?? ''), queryFn: () => workflowDefinitionsApi.getById(id!), enabled: Boolean(id && id !== 'new') });
export const useMyWorkflowTasks = (query: WorkflowTaskQuery) => useQuery({ queryKey: workflowKeys.tasks(query), queryFn: () => workflowTasksApi.my(query) });
export const useWorkflowTask = (id?: string) => useQuery({ queryKey: workflowKeys.task(id ?? ''), queryFn: () => workflowTasksApi.getById(id!), enabled: Boolean(id) });
export const useWorkflowInstance = (id?: string) => useQuery({ queryKey: workflowKeys.instance(id ?? ''), queryFn: () => workflowInstancesApi.getById(id!), enabled: Boolean(id) });
export const useDocumentWorkflow = (type: WorkflowDocumentType, id?: string) => useQuery({ queryKey: workflowKeys.document(type, id ?? ''), queryFn: () => workflowInstancesApi.getByDocument(type, id!), enabled: Boolean(id), retry: false });

export function useSaveWorkflowDefinition() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, definition }: { id?: string; definition: CreateWorkflowDefinitionRequest }) => {
      if (!id || id === 'new') return workflowDefinitionsApi.create(definition);
      await workflowDefinitionsApi.update(id, { name: definition.name, companyId: definition.companyId });
      return workflowDefinitionsApi.replaceSteps(id, definition.steps);
    },
    onSuccess: (definition) => { client.invalidateQueries({ queryKey: workflowKeys.all }); client.setQueryData(workflowKeys.definition(definition.id), definition); },
  });
}

export function useToggleWorkflowDefinition() {
  const client = useQueryClient();
  return useMutation({ mutationFn: ({ id, active }: { id: string; active: boolean }) => active ? workflowDefinitionsApi.activate(id) : workflowDefinitionsApi.deactivate(id), onSuccess: () => client.invalidateQueries({ queryKey: workflowKeys.all }) });
}

export function useWorkflowTaskAction() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action, comments }: { id: string; action: WorkflowTaskDecision; comments: string }) => workflowTasksApi[action](id, comments),
    onSuccess: () => client.invalidateQueries({ queryKey: workflowKeys.all }),
  });
}

export const renumberWorkflowSteps = (steps: WorkflowStepInput[]) => steps.map((step, index) => ({ ...step, stepNo: index + 1 }));
